import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';

// Components
import CardMemberAvatar from '@atoms/CardMemberAvatar';
import ShareIcon from '@icons/ShareIcon';
import FavoriteIcon from '@icons/FavoriteIcon';
import { BodyMedium, H4, LabelLarge } from '@styled';
import { PropertyChip } from '../PropertyChip';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import { ADD_PROPERTY_VIEW } from '@general/graphql/properties.graph';
import getFavoriteHook from '@hooks/getFavoriteHook';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    margin-top: 0;
    padding-top: 0.25rem;

    @media ${mediaQueries.md} {
        display: block;
        margin-bottom: 0;
    }
`;

const IconContainerSmall = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 16px;

    @media ${mediaQueries.md} {
        margin-top: 0;
    }
`;

const IconContainerMediumUp = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    margin-left: 1rem;

    @media ${mediaQueries.md} {
        position: absolute;
        right: 0;
        bottom: 0;
        top: 0;
    }
`;

const PropertyListItem = ({
    id,
    recordId,
    activatedAt,
    activations,
    coverPhotoUrl,
    displayLocation,
    expiresAt,
    internalUrl,
    isFavorite: initialIsFavorite,
    isLoggedIn,
    manager,
    populationDensity,
    purpose,
    size,
    squareFeet,
    title,
    typeOfCenter,
    url,
    showShareIcon = false,
    openInNewTab = false,
    onClick = () => {},
    onDeleteClick,
    hit,
    favorited = () => {},
    unfavorited = () => {},
}) => {
    const { viewer } = useViewer();
    const useFavorite = getFavoriteHook({ type: 'property', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite, setIsFavoriteManually } = useFavorite();
    const [addPropertyViewMutation] = useMutation(ADD_PROPERTY_VIEW);

    useEffect(() => {
        setIsFavoriteManually(initialIsFavorite);
    }, [initialIsFavorite, setIsFavoriteManually]);

    const onFavorite = () => {
        if (isFavorite) {
            removeFavorite(recordId);
            unfavorited({ hit });
        } else {
            saveFavorite(recordId);
            favorited({ hit });
        }
    };

    const addPropertyView = async () => {
        // ignore if user is viewing own property
        if (manager.recordId === viewer.recordId) {
            return;
        }

        // ignore if user is from ICSC
        if (viewer.company.recordId === 5094) {
            return;
        }

        await addPropertyViewMutation({ variables: { id } });
    };

    /**
     * Screens
     */
    const isScreenSmDown = useScreenSmDown();
    const isScreenMinMd = useScreenMinMd();

    const renderIcons = () => {
        return (
            <>
                {showShareIcon && (
                    <div className="mr-1rem">
                        <ShareIcon title={title} url={internalUrl} />
                    </div>
                )}

                <FavoriteIcon
                    id={id}
                    type={typeOfCenter}
                    isLoggedIn={isLoggedIn}
                    isFavorite={isFavorite}
                    onClick={onFavorite}
                    isLoading={isLoadingFavorite}
                />
            </>
        );
    };

    return (
        <div className="list-card py-4 my-0 w-100">
            <div className="d-block d-md-flex">
                <ImageContainer className="card-img-right">
                    {coverPhotoUrl && <img src={coverPhotoUrl} alt={title} className="image-cover" />}
                    {!coverPhotoUrl && (
                        <img src="/assets/images/company-default.svg" className="image-cover" alt={title} />
                    )}
                </ImageContainer>

                <div className="d-block d-md-flex pl-0 w-100">
                    <div className="card-block m-0 ml-2 p-0 pl-3">
                        <Link
                            to={internalUrl}
                            className="card-link"
                            onClick={e => {
                                if (openInNewTab) {
                                    e.preventDefault();
                                    window.open(internalUrl, '_blank');
                                }

                                addPropertyView();
                                onClick({ hit });
                            }}
                        >
                            <H4 className="card-title display6 bold d-inline-block mb-0 align-middle mr-2">{title}</H4>

                            <div className="d-inline-block align-middle">
                                <PropertyChip
                                    activatedAt={activatedAt}
                                    expiresAt={expiresAt}
                                    activations={activations}
                                />
                            </div>

                            {displayLocation && (
                                <div className="mt-0.5rem mb-0.5rem">
                                    <LabelLarge>{displayLocation}</LabelLarge>
                                </div>
                            )}

                            <div className="text-dark-gray">
                                <BodyMedium>{purpose}</BodyMedium>
                                <BodyMedium>{populationDensity}</BodyMedium>
                                <BodyMedium>{typeOfCenter}</BodyMedium>
                                {squareFeet && <BodyMedium>{squareFeet.toLocaleString()} sq ft</BodyMedium>}
                                {!squareFeet && <BodyMedium>{size}</BodyMedium>}
                            </div>

                            <div className="d-flex align-items-center mt-1rem">
                                {manager && (
                                    <>
                                        <CardMemberAvatar
                                            photoURL={manager.photoUrl}
                                            firstName={manager.firstName}
                                            lastName={manager.lastName}
                                            fullName={`${manager.firstName} ${manager.lastName}`}
                                            sizeMobile={26}
                                            sizeDesktop={26}
                                        />
                                        <div className="ml-2">
                                            <BodyMedium>{`${manager.firstName} ${manager.lastName}`}</BodyMedium>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Link>
                    </div>

                    {isScreenSmDown && <IconContainerSmall>{renderIcons()}</IconContainerSmall>}
                </div>

                {isScreenMinMd && <IconContainerMediumUp>{renderIcons()}</IconContainerMediumUp>}
            </div>
        </div>
    );
};

PropertyListItem.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recordId: PropTypes.number,
    activatedAt: PropTypes.string,
    activations: PropTypes.number.isRequired,
    coverPhotoUrl: PropTypes.string,
    displayLocation: PropTypes.string,
    expiresAt: PropTypes.string,
    internalUrl: PropTypes.string,
    isFavorite: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    manager: PropTypes.object,
    populationDensity: PropTypes.string,
    purpose: PropTypes.string,
    size: PropTypes.string,
    squareFeet: PropTypes.number,
    title: PropTypes.string.isRequired,
    typeOfCenter: PropTypes.string,
    url: PropTypes.string,
    showShareIcon: PropTypes.bool,
    onClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    openInNewTab: PropTypes.bool,
    hit: PropTypes.object,
    favorited: PropTypes.func,
    unfavorited: PropTypes.func,
};

export default PropertyListItem;
